import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  PhoneCall, 
  Mail, 
  Clock, 
  MapPin, 
  Home, 
  Megaphone, 
  Info, 
  FileText, 
  Truck, 
  Activity, 
  AlertTriangle, 
  ChevronRight 
} from 'lucide-react';
import { CONTACT_INFO, EMERGENCY_CONTACT } from '../data';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Forside', path: '/', icon: Home },
    { name: 'Meddelelser', path: '/meddelelser', icon: Megaphone },
    { name: 'Information', path: '/information', icon: Info },
    { name: 'Mødereferater', path: '/moedereferater', icon: FileText },
    { name: 'Meddel Flytning', path: '/meddel-flytning', icon: Truck },
    { name: 'Kontakt', path: '/kontakt', icon: Mail },
  ];

  const isActive = (path) => location.pathname === path;

  // Prevent background scrolling when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close sidebar on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <div className="flex flex-shrink-0 items-center">
              <Link to="/" className="flex items-center group">
                <div>
                  <span className="block text-lg font-bold tracking-tight text-slate-900 transition-colors group-hover:text-blue-600">
                    Grindsted Vandværk
                  </span>
                  <span className="block text-xs font-medium text-slate-500">
                    Rent drikkevand siden 1913
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation (xl and up) */}
            <div className="hidden xl:flex xl:items-center xl:gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`rounded-lg px-4 py-2 text-sm transition-all duration-200 ${
                    isActive(item.path)
                      ? 'font-extrabold text-slate-900'
                      : 'font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="ml-4 h-6 w-px bg-slate-200" />
              <a
                href={`tel:${CONTACT_INFO.phoneOffice}`}
                className="ml-4 flex items-center gap-2 rounded-full border border-slate-900 bg-transparent px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-900 transition-all duration-300 transform hover:bg-slate-900 hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-900/10 group"
              >
                <PhoneCall className="h-3.5 w-3.5 text-slate-900 transition-colors duration-300 group-hover:text-white" />
                Tlf: {CONTACT_INFO.phoneOffice}
              </a>
            </div>

            {/* Hamburger Menu Button (visible on tablet and mobile: < xl) */}
            <div className="flex items-center xl:hidden">
              <button
                onClick={() => setIsOpen(true)}
                type="button"
                className="inline-flex items-center justify-center rounded-xl p-2.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none transition-all duration-200"
                aria-label="Åben menu"
              >
                <Menu className="h-6.5 w-6.5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar Menu Drawer (Mobile & Tablet) */}
      {/* Backdrop Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar Container */}
      <div
        className={`fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col justify-between bg-white p-6 sm:p-8 shadow-2xl transition-all duration-300 ease-in-out text-slate-900 ${
          isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
        aria-hidden={!isOpen}
      >
        <div className="flex flex-col h-full overflow-y-auto no-scrollbar -mx-2 px-2">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between pb-5 border-b border-slate-100">
            <div>
              <span className="block text-base font-extrabold tracking-tight text-slate-900">
                Grindsted Vandværk
              </span>
              <span className="block text-xs font-medium text-slate-500">
                Menu & Information
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              type="button"
              className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none transition-all duration-200 border border-slate-200"
              aria-label="Luk menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Live Driftsstatus Widget */}
          <div className="mt-5 bg-emerald-50/60 border border-emerald-100 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <span className="block text-xs font-bold text-emerald-600 uppercase tracking-wider">Driftsstatus</span>
                <span className="block text-sm font-semibold text-slate-800">Normal vandforsyning</span>
              </div>
            </div>
            <Activity className="h-5 w-5 text-emerald-600" />
          </div>

          {/* Sidebar Navigation Links */}
          <div className="mt-6 space-y-1.5">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`group flex items-center justify-between rounded-xl p-3.5 transition-all duration-200 ${
                    active
                      ? 'bg-blue-50 border-l-4 border-blue-600 text-blue-700 font-extrabold'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium'
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg transition-all ${
                      active 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200 group-hover:text-slate-700'
                    }`}>
                      <IconComponent className="h-4.5 w-4.5" />
                    </div>
                    <span className="text-[15px]">{item.name}</span>
                  </div>
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${
                    active ? 'text-blue-600 translate-x-0.5' : 'text-slate-400 group-hover:text-slate-600 group-hover:translate-x-0.5'
                  }`} />
                </Link>
              );
            })}
          </div>

          {/* Akut Vagt Card */}
          <div className="mt-6 bg-amber-50/80 border border-amber-200 rounded-2xl p-4.5">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 text-amber-700 shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <span className="block text-xs font-bold text-amber-700 uppercase tracking-wider">Akut Vagt</span>
                <span className="block text-sm font-bold text-slate-800">{EMERGENCY_CONTACT.company}</span>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Ved akutte problemer som ledningsbrud eller manglende vand.
                </p>
                <a
                  href={`tel:${EMERGENCY_CONTACT.phone}`}
                  className="inline-flex items-center gap-1.5 text-xs font-extrabold text-amber-700 hover:text-amber-800 transition-colors pt-1.5"
                >
                  <PhoneCall className="h-3.5 w-3.5" />
                  Ring vagt: {EMERGENCY_CONTACT.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Sidebar Footer / Contact Info */}
          <div className="mt-8 border-t border-slate-100 pt-6 pb-2 space-y-5">
            <div className="space-y-3.5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Kontaktoplysninger
              </h4>
              
              <div className="flex items-start gap-3 text-sm">
                <Clock className="h-4.5 w-4.5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-slate-700">Kontortid</p>
                  <p className="text-xs text-slate-500">Mandag - Fredag: 10.00 - 12.00</p>
                </div>
              </div>

              <div className="flex items-start gap-3 text-sm">
                <MapPin className="h-4.5 w-4.5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-slate-300">Adresse</p>
                  <p className="text-xs text-slate-500">{CONTACT_INFO.address}, {CONTACT_INFO.zipCity}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 text-sm">
                <Mail className="h-4.5 w-4.5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-slate-300">E-mail</p>
                  <a href={`mailto:${CONTACT_INFO.email}`} className="text-xs text-blue-600 hover:underline">
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
            </div>

            <a
              href={`tel:${CONTACT_INFO.phoneOffice}`}
              className="flex items-center justify-center gap-2.5 rounded-xl bg-blue-600 py-3.5 text-center text-sm font-bold text-white shadow-lg shadow-blue-600/10 transition-all hover:bg-blue-700 hover:shadow-blue-600/20 active:scale-[0.98]"
            >
              <PhoneCall className="h-4 w-4" />
              Ring til kontoret: {CONTACT_INFO.phoneOffice}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
